import type { QuantityKind, UnitFor } from './registry';

/**
 * How many fractional digits a measurement deserves.
 *
 * This module returns a *count*, never a rounded number, and that is the whole design. Rounding
 * mid-pipeline is how a value accumulates error: round to convert, convert to display, round again
 * to format, and 62.137 has become 62.1 twice by different rules. Handing the count to
 * `Intl.NumberFormat` means the value stays exact until the single moment it becomes a string.
 *
 * It also keeps sorting honest. A column sorted on the underlying number and displayed at one
 * decimal shows ties in the right order; a column sorted on pre-rounded values does not.
 */

/**
 * Why the same quantity gets different precision in different places.
 *
 * - `display` — a figure on screen, read at a glance. Digits past the second are noise a driver
 *   cannot act on, and they make a table harder to scan.
 * - `export` — CSV and anything else that will be summed, pivoted or reconciled downstream. Extra
 *   digits cost nothing and truncation is unrecoverable, so this is uniformly looser.
 * - `compact` — a KPI tile, an axis label, a badge. Space is the constraint and the reader wants
 *   the magnitude, not the value.
 */
export const PRECISION_CONTEXTS = ['display', 'export', 'compact'] as const;

export type PrecisionContext = (typeof PRECISION_CONTEXTS)[number];

type DigitsByContext = Readonly<Record<PrecisionContext, number>>;

/**
 * Per-kind defaults.
 *
 * Each row is a judgement about what the reader can use, not about what the sensor reports. Speed
 * is whole numbers because no one reads a tenth of a mile per hour. Pressure is whole numbers in
 * both units because a tyre gauge is not precise to a tenth of a psi and 220.0 kPa implies
 * otherwise. Energy is the outlier at two digits everywhere: kWh figures are billed, and a tenth
 * of a kWh is money.
 */
const DEFAULT_DIGITS: Readonly<Record<QuantityKind, DigitsByContext>> = {
  distance: { display: 1, export: 2, compact: 0 },
  speed: { display: 0, export: 1, compact: 0 },
  capacity: { display: 1, export: 2, compact: 0 },
  fuelConsumption: { display: 1, export: 2, compact: 0 },
  electricConsumption: { display: 1, export: 3, compact: 0 },
  energy: { display: 2, export: 2, compact: 0 },
  pressure: { display: 0, export: 0, compact: 0 },
  temperature: { display: 0, export: 1, compact: 0 },
  emission: { display: 0, export: 2, compact: 0 },
  mass: { display: 0, export: 1, compact: 0 },
};

type UnitOverrides = {
  readonly [K in QuantityKind]?: Readonly<
    Partial<Record<UnitFor<K>, Partial<DigitsByContext>>>
  >;
};

/**
 * The cases where the unit, not the quantity, decides.
 *
 * Precision is really about how many digits carry information at the magnitudes a unit occupies,
 * and units of the same kind can sit orders of magnitude apart. Miles per kWh clusters around 3.5,
 * so one decimal throws away most of the distinction between vehicles; kWh per 100 km sits near 30,
 * where one decimal is plenty. Same kind, different answer.
 *
 * Kept deliberately short. Every entry is a rule someone has to remember, so a unit earns one only
 * when its default is visibly wrong rather than merely arguable.
 */
const UNIT_OVERRIDES: UnitOverrides = {
  electricConsumption: {
    MI_PER_KWH: { display: 2 },
  },
  // A pound per mile is roughly 282 grams per kilometre, so a van that reads 120 g/km reads 0.43
  // lb/mi. At the kind's whole-number default every realistic figure renders as `0 lb/mi`.
  emission: {
    LB_PER_MI: { display: 2 },
  },
};

/**
 * The number of fractional digits to render this measurement with.
 *
 * @param kind - The quantity being shown.
 * @param unit - The unit it will be shown in, which can override the kind's default.
 * @param context - Where it is being shown. Defaults to `display`, the common case.
 * @returns A digit count, for the formatter to apply.
 */
export const digitsFor = <K extends QuantityKind>(
  kind: K,
  unit: UnitFor<K>,
  context: PrecisionContext = 'display'
): number => {
  const overridesForKind = UNIT_OVERRIDES[kind] as
    | Readonly<Partial<Record<string, Partial<DigitsByContext>>>>
    | undefined;

  return overridesForKind?.[unit]?.[context] ?? DEFAULT_DIGITS[kind][context];
};
